import type { Installment, LoanOverViewType } from '../../types/account';

export type BnplNextPaymentKind = 'inProcess' | 'overdue' | 'nextMonth';

export type BnplNextPayment = {
  kind: BnplNextPaymentKind;
  amount: number;
};

const PAID_INSTALLMENT_STATES = ['PAID', 'PREPAID', 'RECOVERED'];

function isUnpaid(installment: Installment) {
  return !PAID_INSTALLMENT_STATES.includes(installment.state)
    && installment.userInvoiceInjectionStatus !== 'SUCCESSFUL';
}

function isInProcess(installment: Installment) {
  return installment.userInvoiceInjectionStatus === 'PENDING';
}

// Las cuotas vencen el día 1 (03:00Z) y las de todas las compras se cobran juntas,
// así que se agrupan por año-mes UTC.
function dueMonthKey(installment: Installment) {
  const date = new Date(installment.expirationDate);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

function sumAmounts(installments: Installment[]) {
  return installments.reduce((acc, installment) => acc + installment.amount, 0);
}

/**
 * Lo que el usuario tiene que pagar ahora: si hay cuotas en cobro o vencidas, solo esas
 * (más morosidad y gastos de cobranza); si no, las cuotas del próximo vencimiento.
 * Retorna null si no hay nada por pagar.
 */
export function getBnplNextPayment(
  loans: LoanOverViewType[],
  now: Date = new Date(),
): BnplNextPayment | null {
  const unpaidInstallments = loans.flatMap((loan) => loan.installments).filter(isUnpaid);
  const dueNowInstallments = unpaidInstallments.filter(
    (installment) => isInProcess(installment) || new Date(installment.expirationDate) <= now,
  );
  const delinquencyAndFeesAmount = loans.reduce(
    (acc, loan) => acc + loan.unpaidDelinquenciesAmount + loan.unpaidCollectionFeesAmount,
    0,
  );

  if (dueNowInstallments.length > 0 || delinquencyAndFeesAmount > 0) {
    return {
      kind: dueNowInstallments.some(isInProcess) ? 'inProcess' : 'overdue',
      amount: sumAmounts(dueNowInstallments) + delinquencyAndFeesAmount,
    };
  }

  if (unpaidInstallments.length === 0) {
    return null;
  }

  const nextDueMonthKey = Math.min(...unpaidInstallments.map(dueMonthKey));
  return {
    kind: 'nextMonth',
    amount: sumAmounts(
      unpaidInstallments.filter((installment) => dueMonthKey(installment) === nextDueMonthKey),
    ),
  };
}
